/* eslint-disable prefer-destructuring */
/**
 * This is the baseline theme object that is designed to provide the raw theme
 * values alongside some common utilities and baseline definitions to meet the
 * Theme UI Spec (see: https://system-ui.com/theme/)
 *
 */

// Note: ..Alt colours are special-use colours for contrast-ratio purposes on certain backgrounds.
const baseColors = {
  nightSky: "#000840",
  nightSkyLight: "#0D2E54",
  newEnergy: "#0DCF5A",
  newEnergyLight: "#0CDF6E",
  hydro: "#1F79FF",
  hydroLight: "#1E97FF",
  solar: "#00B5E0",
  solarLight: "#00C5EE",
  urgentRed: "#E64B38",
  orangeAlert: "#FF6C0E",
  sunshine: "#FFA400",
  optimism: "#F7BE00",
  hydroHover: "#005EEB",
  black: "#535249",
  white: "#ffffff",
  greyWhite: "#E4E4E4",
  gradient: "linear-gradient(rgba(31, 121, 225, 1.0) 4%, rgba(0, 181, 224, 1.0) 29%, rgba(13, 207, 90, 1.0) 95%)",
  neutrals: {
    grey: "#9CA5AD",
    greyLight: "#F2F3F6",
    greyDark: "#F2F3F633",
    darkWhite: "#D0DAE2",
  },
};

const colors = {
  ...baseColors,
  text: baseColors.black,
  background: baseColors.white,
  primary: baseColors.nightSky,
  secondary: baseColors.newEnergy,
  tertiary: baseColors.solar,
  accent: baseColors.hydro,
  greyLight: baseColors.neutrals.greyLight,
  greyDark: baseColors.neutrals.greyDark,
  borderColor: baseColors.neutrals.darkWhite,
  muted: baseColors.neutrals.grey,
  disabled: baseColors.neutrals.greyLight,
  danger: baseColors.urgentRed,
  hover: baseColors.hydroHover,
  oldBrandingPrimary: "#92C04E",
};

const fonts = {
  current: "Montserrat, sans-serif",
};

export interface IAliasedFontSizes extends Array<number> {
  title?: number;
  heading?: number;
  body?: number;
}

const fontSizes: IAliasedFontSizes = [14, 16, 18, 20, 22, 24, 32, 40, 48, 52];
fontSizes.title = fontSizes[6];
fontSizes.heading = fontSizes[5];
fontSizes.body = fontSizes[2];

const fontWeights = {
  light: 300,
  normal: "normal",
  bold: "bold",
};

const lineHeights = {
  title: "58px",
  heading: "48px",
  body: "32px",
  small: "24px",
};

export interface IAliasedSpaces extends Array<number> {
  xxt?: number;
  xt?: number;
  t?: number;
  m?: number;
  l?: number;
  xl?: number;
}

const space: IAliasedSpaces = [0, 4, 8, 16, 32, 64, 96];
space.xxt = space[1];
space.xt = space[2];
space.t = space[3];
space.m = space[4];
space.l = space[5];
space.xl = space[6];

const sizes = [0, 2, 4, 8, 12, 16, 20, 24, 28, 32, 48, 64, 80, 96];

// the breakpoints come from
// https://getbootstrap.com/docs/4.0/layout/overview/#responsive-breakpoints
const breakpoints = ["576px", "768px", "992px", "1200px"];

export const mediaQueries = {
  // extraSmallDevice: `@media screen and (max-width: ${breakpoints[0]})`,
  // Small devices (landscape phones, 576px and up)
  smallDevice: `@media screen and (min-width: ${breakpoints[0]})`,
  // Medium devices (tablets, 768px and up)
  mediumDevice: `@media screen and (min-width: ${breakpoints[1]})`,
  // Large devices (desktops, 992px and up)
  largeDevice: `@media screen and (min-width: ${breakpoints[2]})`,
  // Extra large devices (large desktops, 1200px and up)
  extraLargeDevice: `@media screen and (min-width: ${breakpoints[3]})`,
};

export const screenQueries = {
  extraSmallDevice: `(max-width: ${breakpoints[0]})`,
  // Small devices (landscape phones, 576px and up)
  smallDevice: `(min-width: ${breakpoints[0]})`,
  // Medium devices (tablets, 768px and up)
  mediumDevice: `(min-width: ${breakpoints[1]})`,
  // Large devices (desktops, 992px and up)
  largeDevice: `(min-width: ${breakpoints[2]})`,
  // Extra large devices (large desktops, 1200px and up)
  extraLargeDevice: `(min-width: ${breakpoints[3]})`,
}

const radii = {
  button: 4,
  input: 5,
};

export const theme = {
  // Structure & Utility
  space,
  sizes,
  breakpoints,
  mediaQueries,
  radii,
  // Colours
  colors,
  // Typography
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  // Elements
};
